import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { DatesProvider } from '@mantine/dates';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import './styles.css';
// Новый вид дизайнера — за переключателем data-design="warm" (правило 295)
import './design/warm/fonts.css';
import './design/warm/tokens.css';
import './design/warm/overrides.css';

import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { theme } from './theme';
import { warmTheme, warmCssVariables } from './design/warm/theme';
import { useWarmDesign } from './design/design';
import { isNativeApp, NetworkError } from './api/client';

dayjs.locale('ru');

/**
 * Service worker — только в браузере.
 *
 * В APK файлы сборки и так лежат внутри приложения: офлайн работает без
 * него, а данные держит Dexie. Зато кэш service worker'а переживает
 * переустановку APK и продолжает отдавать старую сборку — замерщик
 * работает по прошлогодним формулам, и на экране это ничем не отличается
 * от свежей версии. Ровно так и случилось при первом обновлении планшета.
 */
if (!isNativeApp() && 'serviceWorker' in navigator) {
  /*
   * РЕГИСТРИРУЕМ САМИ, БЕЗ ПОМОЩНИКА ПЛАГИНА (правило 211). В режиме
   * autoUpdate `virtual:pwa-register` перезагружал страницу в ту же
   * секунду, когда вставал новый worker, — посреди набранной формы, — и
   * из-за этого обновление в браузере держали на кнопке. Новый worker
   * по-прежнему встаёт сам и забирает свежую сборку в фоне, а КОГДА
   * перезагрузиться, решает `VersionNotice`: при переходе на другой
   * экран, в скрытой вкладке или после минуты простоя — и никогда поверх
   * несохранённого.
   */
  void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
}

/**
 * Снос service worker'а, оставшегося от прежних сборок.
 *
 * Перестать регистрировать нового мало: уже зарегистрированный живёт
 * дальше сам по себе. Он переживает переустановку APK, перехватывает
 * загрузку страницы и отдаёт свой кэш — планшет показывает прошлую
 * сборку, хотя внутри приложения лежит свежая. Обновляется такой кэш
 * с отставанием на один запуск, поэтому расхождение то появляется,
 * то пропадает, и списывается на «показалось».
 *
 * Снимаем регистрацию и чистим кэши, после чего перезагружаем экран
 * один раз. Замеры это не трогает: черновики и очередь отправки лежат
 * в IndexedDB, а не в кэше service worker'а.
 */
if (isNativeApp() && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.getRegistrations().then(async (registrations) => {
    if (registrations.length === 0) return;
    await Promise.all(registrations.map((r) => r.unregister()));
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
    // Страница уже отрисована из старого кэша — показать свежую сборку
    // можно только перезагрузкой. Она одноразовая: регистраций больше нет
    window.location.reload();
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Повторять запрос при отсутствии сети бессмысленно: данные всё равно
      // берутся из локальной базы, а лишние попытки сажают батарею планшета
      retry: (failureCount, error) => !(error instanceof NetworkError) && failureCount < 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Сторож запуска в index.html (правило 237) ждёт этой отметки: без неё через
// 15 секунд он покажет «не загрузилась» вместо белого экрана
(window as { __blagodarStarted?: boolean }).__blagodarStarted = true;

/** Тема — по переключателю вида: меняется на лету, без перезагрузки */
function ThemedProvider({ children }: { children: ReactNode }) {
  const warm = useWarmDesign();
  return (
    <MantineProvider
      theme={warm ? warmTheme : theme}
      cssVariablesResolver={warm ? warmCssVariables : undefined}
      defaultColorScheme="light"
    >
      {children}
    </MantineProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemedProvider>
      <DatesProvider settings={{ locale: 'ru', firstDayOfWeek: 1, weekendDays: [0, 6] }}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <Notifications position="top-right" />
              <App />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </DatesProvider>
    </ThemedProvider>
  </StrictMode>,
);
